import { useQuery, keepPreviousData } from "@tanstack/react-query";
import { format } from "date-fns";
import { revenueApi } from "@/lib/api";
import { contractSelectionParams, isEmptySelection, type ContractSelection } from "@/lib/royalty";
import { useOrganizationStore } from "@/stores/organizationStore";

interface UseRoyaltyStatementPreviewChecksParams {
  selection: ContractSelection;
  startDate?: Date;
  endDate?: Date;
  enabled?: boolean;
}

export function useRoyaltyStatementPreviewChecks({
  selection,
  startDate,
  endDate,
  enabled = true,
}: UseRoyaltyStatementPreviewChecksParams) {
  const { currentLabel } = useOrganizationStore();
  const periodStart = startDate ? format(startDate, "yyyy-MM-dd") : "";
  const periodEnd = endDate ? format(endDate, "yyyy-MM-dd") : "";
  const params = contractSelectionParams(selection);

  const { data, isLoading } = useQuery({
    queryKey: [
      "royalty-statement-preview-checks",
      currentLabel?.id,
      periodStart,
      periodEnd,
      selection.mode,
      [...selection.contracts].sort().join(","),
    ],
    queryFn: () =>
      revenueApi.getRoyaltyStatementPreviewChecks({
        label_id: currentLabel?.id as string,
        period_start: periodStart,
        period_end: periodEnd,
        ...params,
      }),
    // Nothing selected has nothing to check.
    enabled: enabled && !!currentLabel && !!startDate && !!endDate && !isEmptySelection(selection),
    // Every tick changes the selection (and the key); keep the last result on
    // screen so the banners and revenue-statements card don't unmount mid-fetch.
    placeholderData: keepPreviousData,
    // Unsettled revenue blocks creation, and it can be settled in another tab.
    // ponytail: poll only while something is unsettled; refetchIntervalInBackground
    // stays off, so a hidden tab costs nothing.
    refetchInterval: (query) =>
      query.state.data?.revenue_statements.some((s) => !s.is_settled) ? 30_000 : false,
  });

  return { checks: data ?? null, isLoading };
}
